import { createElement } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import type { MarketFilterTab } from "@/shared/types/bondFilters";
import {
  filtersToSearchParams,
  hasFilterSearchParams,
  NON_FILTER_PARAMS,
} from "@/shared/utils/filterUrlParams";
import { maskFiltersToTab } from "@/shared/utils/marketFilterScope";
import { loadMarketFilters } from "@/shared/utils/marketTabFilterStore";
import RouteFallback from "@/shared/ui/RouteFallback";
import { RouteErrorFallback } from "@/shared/ui/ErrorFallback";
import { RootLayout, HomePageEntry, InvitePageEntry, HealthPage } from "@/router";
import AgentRenderDemoRoute from "@/domains/agent/ui/AgentRenderDemoRoute";
import PostHogRoot from "@/providers/PostHogRoot";

// Replaces TanStack Router's default JSON-based search serialisation with a
// URLSearchParams repeated-param format. Our filter URLs use repeated params
// for arrays (?sectors=A&sectors=B) which the default JSON encoding transforms
// to ?sectors=["A","B"], causing URLSearchParams-based parsing in our hooks to
// silently drop multi-value filter selections (arrays coerce to comma strings).
function parseSearch(searchStr: string): Record<string, string | string[]> {
  const params = new URLSearchParams(searchStr);
  const result: Record<string, string | string[]> = {};
  const seen = new Set<string>();
  for (const key of params.keys()) {
    if (seen.has(key)) continue;
    seen.add(key);
    const values = params.getAll(key);
    result[key] = values.length === 1 ? values[0] : values;
  }
  return result;
}

function stringifySearch(search: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, val] of Object.entries(search)) {
    if (val == null) continue;
    if (Array.isArray(val)) {
      for (const v of val) params.append(key, String(v));
    } else if (
      typeof val === "string" ||
      typeof val === "number" ||
      typeof val === "boolean" ||
      typeof val === "bigint"
    ) {
      params.set(key, String(val));
    }
  }
  const str = params.toString();
  return str ? `?${str}` : "";
}

// Unknown URLs render inside the shell. The splat is a full match for any path
// that is not a real page, including an unknown section under a known prefix,
// so the settings sidebar does not stay up. The root not-found covers a
// notFound() thrown from a page that did match.
const NotFoundPageRoute = lazyRouteComponent(() => import("@/routes/NotFoundRoute"));

const rootRoute = createRootRoute({
  component: () => createElement(PostHogRoot, null, createElement(RootLayout)),
  notFoundComponent: NotFoundPageRoute,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomePageEntry,
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite",
  component: InvitePageEntry,
});

const marketsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/markets",
  component: lazyRouteComponent(() => import("@/routes/MarketsRoute")),
  beforeLoad: ({ location }) => {
    const sp = new URLSearchParams(location.searchStr);
    const tab = (sp.get("tab") ?? "quotes") as MarketFilterTab;

    // Messages tab has its own URL schema — don't inject market filters.
    if ((tab as string) === "messages") return;

    // If the URL already carries filter params, it's authoritative — leave it alone.
    if (hasFilterSearchParams(sp)) return;

    // No filter params: restore the last canonical state from localStorage.
    const stored = loadMarketFilters();
    const visible = maskFiltersToTab(stored, tab);
    const params = filtersToSearchParams(visible);
    // A lone `tab` param is not a filter: for non-default tabs (e.g. rfqs)
    // `filtersToSearchParams` always emits `tab=rfqs`, so guarding on a
    // non-empty string would redirect to the identical URL forever. Only
    // redirect when there is an actual filter to restore.
    if (!hasFilterSearchParams(params)) return;

    // Preserve non-filter params (bond drawer, expanded panel, etc.).
    for (const [key, value] of sp.entries()) {
      if (NON_FILTER_PARAMS.has(key)) params.set(key, value);
    }

    throw redirect({ href: `/markets?${params.toString()}`, replace: true });
  },
});

const watchlistsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/watchlists",
  component: lazyRouteComponent(() => import("@/routes/WatchlistsRoute")),
});

const aiAssistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/ai-assist",
  component: lazyRouteComponent(() => import("@/routes/AiAssistRoute")),
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: lazyRouteComponent(() => import("@/routes/SettingsRoute")),
});

/** MUI `breakpoints.down("md")`: phones and portrait tablets stay on the menu. */
function isCompactSettingsViewport(): boolean {
  if (typeof globalThis.matchMedia !== "function") return false;
  return globalThis.matchMedia("(max-width:899.95px)").matches;
}

// Bare /settings is the full-screen section menu on small screens. Wider
// screens have no page of their own here and land on the first sidebar entry.
const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  beforeLoad: () => {
    if (!isCompactSettingsViewport()) {
      throw redirect({ to: "/settings/profile", replace: true });
    }
  },
});

const settingsProfileRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "profile",
  component: lazyRouteComponent(() => import("@/routes/ProfileRoute")),
});

const settingsMessageMetricsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "message-metrics",
  component: lazyRouteComponent(() => import("@/routes/MessageMetricsRoute")),
});

const settingsAiMemoryRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "ai-memory",
  component: lazyRouteComponent(() => import("@/routes/AgentMemoriesRoute")),
});

const settingsExportsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "exports",
  component: lazyRouteComponent(() => import("@/routes/ExportsRoute")),
});

const settingsBenchmarkCurvesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "benchmark-curves",
  component: lazyRouteComponent(() => import("@/routes/BenchmarkCurvesRoute")),
});

const settingsUsageRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "usage",
  component: lazyRouteComponent(() => import("@/routes/UsageRoute")),
});

const settingsFirmsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "firms",
  component: lazyRouteComponent(() => import("@/routes/FirmsRoute")),
});

const settingsFirmDetailRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "firms/$firmId",
  component: lazyRouteComponent(() => import("@/routes/FirmDetailRoute")),
});

// These pages used to sit at the top level before the settings section
// absorbed them. Bookmarks and links already sent out still point at the old
// paths, so they redirect rather than land on a not-found.
const MOVED_PATHS: ReadonlyArray<readonly [from: string, to: string]> = [
  ["/profile", "/settings/profile"],
  ["/exports", "/settings/exports"],
  // Same page as before, renamed to "Firm Usage" in the sidebar.
  ["/stats", "/settings/usage"],
  ["/firms", "/settings/firms"],
];

const movedRoutes = MOVED_PATHS.map(([from, to]) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path: from,
    beforeLoad: () => {
      throw redirect({ href: to, replace: true });
    },
  }),
);

const movedFirmDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/firms/$firmId",
  beforeLoad: ({ params }) => {
    throw redirect({
      href: `/settings/firms/${encodeURIComponent(params.firmId)}`,
      replace: true,
    });
  },
});

const healthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/health",
  component: HealthPage,
});

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/$",
  component: NotFoundPageRoute,
});

// Dev-only demo surface for the agent render registry (#670). Registered only in
// dev builds, so the route does not exist in production; the page itself is lazy.
const devOnlyRoutes = import.meta.env.DEV
  ? [
      createRoute({
        getParentRoute: () => rootRoute,
        path: "/dev/agent-render",
        component: AgentRenderDemoRoute,
      }),
    ]
  : [];

const routeTree = rootRoute.addChildren([
  indexRoute,
  inviteRoute,
  marketsRoute,
  watchlistsRoute,
  aiAssistRoute,
  settingsRoute.addChildren([
    settingsIndexRoute,
    settingsProfileRoute,
    settingsMessageMetricsRoute,
    settingsAiMemoryRoute,
    settingsExportsRoute,
    settingsBenchmarkCurvesRoute,
    settingsUsageRoute,
    settingsFirmsRoute,
    settingsFirmDetailRoute,
  ]),
  ...movedRoutes,
  movedFirmDetailRoute,
  healthRoute,
  ...devOnlyRoutes,
  notFoundRoute,
]);

export const router = createRouter({
  routeTree,
  parseSearch,
  stringifySearch,
  // Route components load lazily, so warm the chunk while the user is still
  // deciding: hovering or focusing a link starts the fetch.
  defaultPreload: "intent",
  defaultPreloadDelay: 50,
  // Only show the spinner when a chunk actually takes a moment to arrive,
  // otherwise fast local navigations flash it.
  defaultPendingComponent: RouteFallback,
  defaultPendingMs: 150,
  // A route chunk that never arrives (a deploy replaced the hashed assets under
  // a running tab) would otherwise take the app down to a blank page.
  defaultErrorComponent: RouteErrorFallback,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
